import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MapPin, CalendarDays, ArrowRight, Users, Baby } from 'lucide-react';
import { SEASON } from '../../shared/season.js';

type AgeGroup = 'teens' | 'kids';

const handleGroupClick = (group: AgeGroup) => {
  try {
    sessionStorage.setItem('sw_ageGroup', group);
  } catch {
    // sessionStorage may be unavailable (private mode / SSR); proceed silently
  }
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'click', {
      event_category: 'engagement',
      event_label: `hero_cta_${group}`,
    });
  }
  document.getElementById('registration')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Hero: React.FC = () => {
  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Video background — blurred ambient texture, not the focal point */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          className="hero-video absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hero-poster.jpg"
          aria-hidden="true"
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        {/* Poster fallback shown when video is hidden by prefers-reduced-motion */}
        <img
          src="/hero-poster.jpg"
          alt=""
          className="hero-poster-fallback absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        {/* Brand color wash — ties the video into the palette and hides artefacts */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/35 via-slate-900/25 to-primary-900/35 md:from-indigo-900/50 md:via-slate-900/40 md:to-primary-900/50 mix-blend-multiply" />
        {/* Readability overlay — lighter on mobile so the video is more evident */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/65 md:from-black/55 md:via-black/35 md:to-black/80" />
        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none md:hidden" style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)' }} />
        <div className="absolute inset-0 pointer-events-none hidden md:block" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)' }} />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-24 md:py-28 flex flex-col items-center justify-center min-h-screen text-center">
        {/* Free badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative inline-flex items-center gap-2 bg-gradient-to-r from-emerald-400 to-green-500 text-white px-5 py-2 rounded-full text-sm md:text-base font-bold uppercase tracking-wider shadow-xl mb-8"
        >
          <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
          <span>Pilnīgi bezmaksas</span>
          <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
        </motion.div>

        {/* Wordmark */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-6"
        >
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tight text-white drop-shadow-2xl">
            SUPER WEEK
          </h1>
          <div className="mt-2 h-1 w-32 mx-auto rounded-full bg-gradient-to-r from-primary-400 via-secondary-400 to-accent-400" />
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="text-lg md:text-2xl text-white/95 font-medium max-w-2xl mb-6 drop-shadow-lg"
        >
          Vasaras nometne bērniem un jauniešiem
        </motion.p>

        {/* Location pill */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white px-4 py-2 rounded-full text-sm md:text-base mb-12 shadow-lg"
        >
          <MapPin className="w-4 h-4 md:w-5 md:h-5 text-accent-300" />
          <span className="font-medium">Stadions Viļānos · {SEASON.weekRange}</span>
        </motion.div>

        {/* Two camp cards — the main CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 w-full max-w-4xl"
        >
          {/* Teens card */}
          <motion.button
            type="button"
            onClick={() => handleGroupClick('teens')}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="group relative text-left bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/25 hover:border-accent-300/60 rounded-3xl p-6 md:p-7 shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shadow-lg">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-accent-200 font-semibold">Nometne #1</p>
                <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">{SEASON.groups.teens.label}</h3>
              </div>
            </div>

            <div className="flex items-center gap-2 text-white/90 mb-5">
              <CalendarDays className="w-5 h-5 text-accent-300" />
              <span className="font-semibold text-base md:text-lg">{SEASON.groups.teens.datesWithYear}</span>
              <span className="text-white/60 text-sm">· {SEASON.groups.teens.weekdaysShort}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">2 dienas piedzīvojumu</span>
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold px-5 py-2.5 rounded-full shadow-lg group-hover:shadow-accent-500/40 transition-shadow">
                {SEASON.registrationOpen ? 'Pieteikties' : 'Drīzumā'}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </motion.button>

          {/* Kids card */}
          <motion.button
            type="button"
            onClick={() => handleGroupClick('kids')}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            className="group relative text-left bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/25 hover:border-primary-300/60 rounded-3xl p-6 md:p-7 shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                <Baby className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-primary-200 font-semibold">Nometne #2</p>
                <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">{SEASON.groups.kids.label}</h3>
              </div>
            </div>

            <div className="flex items-center gap-2 text-white/90 mb-5">
              <CalendarDays className="w-5 h-5 text-primary-300" />
              <span className="font-semibold text-base md:text-lg">{SEASON.groups.kids.datesWithYear}</span>
              <span className="text-white/60 text-sm">· {SEASON.groups.kids.weekdaysShort}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">2 dienas jautrības</span>
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold px-5 py-2.5 rounded-full shadow-lg group-hover:shadow-primary-500/40 transition-shadow">
                {SEASON.registrationOpen ? 'Pieteikties' : 'Drīzumā'}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
